import { useCallback, useEffect, useRef, useState } from "react";

export interface LiveData<T> {
  data: T | null;
  /** Set when the most recent attempt failed; `data` may still be good. */
  error: string | null;
  /** When `data` was fetched, for reporting how old it is. */
  loadedAt: number | null;
  /**
   * Refetch now, without clearing what is on screen. For after an action
   * whose result should be visible immediately rather than at the next tick.
   */
  reload: () => void;
}

/**
 * Fetches on mount and then on an interval, holding the last good data.
 *
 * Two decisions worth stating, because both are about not overstating what is
 * known:
 *
 * A failed refresh sets `error` but leaves `data` and `loadedAt` alone. The
 * caller is expected to keep rendering the rows and say how old they are --
 * blanking the table on one dropped request throws away good information, and
 * silently leaving it up pretends nothing happened.
 *
 * Refreshes are skipped while the tab is hidden and run immediately when it
 * becomes visible again. A background tab is not being read, so polling it
 * only burns queries; the return to it is precisely when the stalest data is
 * about to be looked at.
 */
export function useLiveData<T>(
  load: () => Promise<T>,
  intervalMs: number,
  deps: unknown[] = [],
): LiveData<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  // The caller passes a new closure every render; keeping it in a ref stops
  // that from tearing down and restarting the timer on each one.
  const loadRef = useRef(load);
  loadRef.current = load;

  // Set by the effect each time it runs, so `reload` always drives the
  // current subscription and never a torn-down one.
  const runRef = useRef<() => void>(() => {});
  const reload = useCallback(() => runRef.current(), []);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    // Deps changed, which means a different subject (another device). Its
    // predecessor's data must not linger on screen under the new heading.
    setData(null);
    setError(null);
    setLoadedAt(null);

    const run = async () => {
      // A slow response must not let ticks stack up behind it.
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const result = await loadRef.current();
        if (cancelled) return;
        setData(result);
        setError(null);
        setLoadedAt(Date.now());
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        inFlight = false;
      }
    };

    runRef.current = () => void run();
    void run();

    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void run();
    }, intervalMs);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void run();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      runRef.current = () => {};
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);

  return { data, error, loadedAt, reload };
}
